import React, { useEffect, useRef } from 'react';

const FORMATS = {
  leaderboard: { minHeight: 90, maxWidth: 728, label: '728×90' },
  rectangle: { minHeight: 250, maxWidth: 336, label: '336×250' },
  inArticle: { minHeight: 280, maxWidth: '100%', label: 'Responsive' },
};

const SLOT_ENV_KEYS = {
  'below-header': 'REACT_APP_ADSENSE_SLOT_HEADER',
  'between-tool-content': 'REACT_APP_ADSENSE_SLOT_BETWEEN',
  'in-content': 'REACT_APP_ADSENSE_SLOT_IN_CONTENT',
};

const getSlotId = (placement) => {
  const envKey = SLOT_ENV_KEYS[placement];
  if (!envKey) return null;
  return process.env[envKey] || null;
};

const AdUnit = ({ placement = 'between-tool-content', format = 'leaderboard', className = '' }) => {
  const insRef = useRef(null);
  const pushedRef = useRef(false);
  const clientId = process.env.REACT_APP_ADSENSE_CLIENT;
  const slotId = getSlotId(placement);
  const size = FORMATS[format] || FORMATS.leaderboard;
  const hasAds = Boolean(clientId && slotId);

  useEffect(() => {
    if (!hasAds || !insRef.current || pushedRef.current) return;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch (e) {
      console.warn('[AdUnit] adsbygoogle push failed', e);
    }
  }, [hasAds, placement, slotId]);

  useEffect(() => {
    if (!hasAds) return undefined;

    const existing = document.querySelector('script[data-ad-sense="true"]');
    if (existing) return undefined;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
    script.crossOrigin = 'anonymous';
    script.setAttribute('data-ad-sense', 'true');
    document.head.appendChild(script);

    return () => {
      // Keep script loaded for SPA navigation
    };
  }, [hasAds, clientId]);

  return (
    <div
      className={`ad-unit ad-unit--${placement} flex justify-center w-full ${className}`}
      style={{
        minHeight: size.minHeight,
        maxWidth: typeof size.maxWidth === 'number' ? size.maxWidth : undefined,
        margin: '0 auto',
        contain: 'layout',
      }}
      aria-label="Advertisement"
    >
      {hasAds ? (
        <ins
          ref={insRef}
          className="adsbygoogle block w-full"
          style={{ display: 'block', minHeight: size.minHeight }}
          data-ad-client={clientId}
          data-ad-slot={slotId}
          data-ad-format={format === 'inArticle' ? 'auto' : undefined}
          data-full-width-responsive={format === 'inArticle' ? 'true' : undefined}
        />
      ) : (
        <div
          className="flex items-center justify-center w-full bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg"
          style={{ minHeight: size.minHeight, maxWidth: size.maxWidth }}
        >
          <div className="text-center p-4">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Advertisement</div>
            <div className="text-gray-600 dark:text-gray-500 text-xs">{size.label}</div>
            <div className="text-gray-500 dark:text-gray-600 text-xs mt-1">
              {placement} · Ad slot ready
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdUnit;
